import { ReactElement } from 'react';

import { Box } from '../../Box';
import { Typography } from '../../Typography';
import { Tooltip } from '../../Tooltip';

import { scaleBarInputConfigs } from './ScaleBarInput.configs';

const SCALE_BAR_ELEMENTS_COUNT = 5;

/**
 * Функция для определения конфигурации на основе текущего значения и целевого значения.
 *
 * @param value - Текущее значение шкалы.
 * @param target - Целевое значение для шкалы.
 * @returns  Объект конфигурации для шкалы.
 */

const getConfig = (value: number, target: number) => {
  let type: keyof typeof scaleBarInputConfigs;

  if (value < target) type = 'low';
  else if (value === target) type = 'normal';
  else type = 'high';

  return scaleBarInputConfigs[type];
};

/**
 * Свойства компонента {@link ScaleBarInput}
 */
interface ScaleBarProps {
  /**
   * Целевое значение для шкалы.
   */
  target?: number;

  /**
   * Текущее значение шкалы.
   */
  value?: number;

  /**
   * Флаг отображения метки.
   */
  showLabel?: boolean;

  /**
   * Пользовательская функция для рендера подсказки.
   */
  renderTooltip?: (index: number) => ReactElement | undefined;

  /**
   * Размер элементов шкалы.
   */
  size?: 'sm' | 'md';

  /**
   * Цветовая схема шкалы.
   */
  color?: 'text' | 'stroke';

  /**
   * Коллбэк-функция, вызываемая при изменении значения.
   */
  onChange?: (value: number) => void;

  /**
   * Флаг, указывающий, отключена ли шкала.
   */
  disabled?: boolean;
}

/**
 * Компонент ScaleBarInput используется для отображения шкалы с интерактивными элементами.
 * Он поддерживает отображение меток, подсказок и обработку изменений значения.
 *
 * @public
 * @param target - Целевое значение для шкалы.
 * @param value - Текущее значение шкалы.
 * @param showLabel - Флаг отображения метки.
 * @param renderTooltip - Пользовательская функция для рендера подсказки.
 * @param size - Размер элементов шкалы.
 * @param color - Цветовая схема шкалы.
 * @param onChange - Коллбэк-функция, вызываемая при изменении значения.
 * @param disabled - Флаг, указывающий, отключена ли шкала.
 * @returns Компонент ScaleBarInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ScaleBarProps}
 */
export const ScaleBarInput = ({
  target = 0,
  value = 0,
  renderTooltip,
  size = 'md',
  color = 'text',
  onChange,
  showLabel = true,
  disabled,
}: ScaleBarProps) => {
  const config = getConfig(value, target);

  const handleChange = (i: number) => {
    if (disabled) {
      return;
    }
    onChange?.(i);
  };

  return (
    <Box display="flex" flexDirection="column" gap="8" width="100%">
      {showLabel && (
        <Box>
          <Typography variant="caption" color={config.text.color}>
            {config.text.label}
          </Typography>
        </Box>
      )}
      <Box display="flex" alignItems="center" gap="4">
        {new Array(SCALE_BAR_ELEMENTS_COUNT).fill(null).map((_, index) => {
          const tooltip = renderTooltip?.(index);
          const colorCell =
            color === 'stroke' ? config.level.strokeColor : config.level.color;

          return (
            <Tooltip
              disabled={!tooltip}
              title={tooltip || ''}
              placement="bottom"
              key={index}
            >
              <Box
                display="flex"
                flexGrow={1}
                height={size === 'md' ? 8 : 4}
                radius="m"
                bgcolor={index < value ? colorCell : 'stroke.primary'}
                onClick={() => handleChange(index + 1)}
                cursor={onChange && !disabled ? 'pointer' : 'default'}
                key={index}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};
